/**
 * @fileoverview TypeScript types for the bootstrap architecture.
 *
 * This module defines comprehensive types for the two-phase bootstrap process:
 * 1. Critical phase - Blocks initial UI render, contains session-critical data
 * 2. Cache phase - Non-blocking background load of reference data
 *
 * The bootstrap architecture supports:
 * - React Strict Mode race condition prevention via module-level guards
 * - Retry logic with exponential backoff for transient failures
 * - Proper cleanup via AbortController on component unmount
 * - Incremental hydration for improved perceived performance
 *
 * @see react-ui/HOOKS_BEST_PRACTICES.md for hook patterns
 * @see docs/adr/0002-bootstrap-state-hydration.md for architecture decisions
 */

// =============================================================================
// API Response Types
// =============================================================================

/**
 * Response structure from the `/api/bootstrap/critical` endpoint.
 *
 * Contains all session-critical data required for initial UI render:
 * - Images: Map images, NPC portraits, item icons
 * - Session: Campaign state, combat, plots, lore, NPCs, maps, etc.
 * - Settings: User preferences (theme, grid visibility, active tabs)
 *
 * Note: csrfToken was removed per ADR-032 (Pure Bearer Token Authentication).
 * Bearer tokens provide inherent CSRF protection since they are not automatically
 * attached by the browser like cookies.
 */
export interface CriticalBootstrapResponse {
  /** Array of image objects for the image library */
  images: unknown[];
  /** Session state containing campaign, combat, plots, lore, NPCs, maps, etc. */
  session: Record<string, unknown>;
  /** User settings including theme, grid visibility, and active tabs */
  settings: Record<string, unknown>;
  /**
   * CSRF token for subsequent requests.
   * @deprecated Kept for backward compatibility but no longer used per ADR-032.
   */
  csrfToken?: string;
}

/**
 * Response structure from the `/api/bootstrap/cache` endpoint.
 *
 * Contains reference data that is useful but not critical for initial render.
 * The UI can display and function without this data, with features becoming
 * available as the cache loads in the background.
 *
 * This data is typically shared across campaigns and changes infrequently,
 * making it ideal for background loading.
 *
 * Note: This type is a subset of CacheState. Fields not included here are
 * either loaded during critical bootstrap or managed separately (e.g., monsterRoster,
 * deities are loaded with critical data; categories may be loaded on demand).
 */
export interface CacheBootstrapResponse {
  /** Array of item definitions (weapons, armor, equipment) */
  items?: unknown[];
  /** Array of spell definitions */
  spells?: unknown[];
  /** Array of treasure definitions */
  treasures?: unknown[];
  /** Array of moon definitions for calendar systems */
  moons?: unknown[];
  /** Array of weather condition definitions */
  weatherConditions?: unknown[];
  /** Monster roster - may be included in cache bootstrap */
  monsterRoster?: unknown[];
  /** Deity definitions */
  deities?: unknown[];
  /** Image categories for organization */
  imageCategories?: unknown[];
  /** Lore categories for organization */
  loreCategories?: unknown[];
  /** Soundscape categories for organization */
  soundscapeCategories?: unknown[];
  /** Terrain types for map regions */
  terrainTypes?: unknown[];
  /** Soundscape definitions */
  soundscapes?: unknown[];
}

// =============================================================================
// Bootstrap Result Types
// =============================================================================

/**
 * Result type for the useBootstrapCoordinator hook.
 *
 * Provides granular loading states for two-phase bootstrap hydration,
 * allowing the UI to render with critical data while cache data loads
 * in the background.
 *
 * @example
 * ```tsx
 * const { isLoading, isCacheLoading, isError, error } = useBootstrapCoordinator();
 *
 * if (isLoading) return <LoadingSpinner />;
 * if (isError) return <ErrorDisplay error={error} />;
 *
 * // Render main UI - cache may still be loading
 * return (
 *   <MainApp>
 *     {isCacheLoading && <CacheLoadingIndicator />}
 *   </MainApp>
 * );
 * ```
 */
export interface AppInitializerState {
  /**
   * True while critical data is being fetched.
   * When true, the main application UI should not render - show a loading screen.
   */
  isLoading: boolean;

  /**
   * True while cache data is being fetched in the background.
   * The main UI can render while this is true; only cache-dependent features
   * need to show loading indicators or gracefully degrade.
   */
  isCacheLoading: boolean;

  /**
   * True if a fatal error occurred during critical data fetch.
   * Cache fetch errors are non-fatal and do not set this flag.
   */
  isError: boolean;

  /**
   * The error object if a fatal error occurred during critical bootstrap.
   * Will be null if no error occurred.
   * May be an instance of BootstrapTimeoutError, RetryExhaustedError, ApiError, etc.
   */
  error: Error | null;
}

/**
 * Alias for backward compatibility.
 * The BootstrapResult type matches AppInitializerState exactly.
 */
export type BootstrapResult = AppInitializerState;

/**
 * Result type for the critical bootstrap phase.
 *
 * Used internally to communicate the result of the critical fetch operation.
 * Success includes the parsed response data; failure includes the error.
 */
export interface CriticalBootstrapResult {
  /** Whether the critical bootstrap completed successfully */
  success: boolean;
  /** The parsed response data, present only if success is true */
  data?: CriticalBootstrapResponse;
  /** The error that occurred, present only if success is false */
  error?: Error;
}

/**
 * Result type for the cache bootstrap phase.
 *
 * Used internally to communicate the result of the cache fetch operation.
 * Cache failures are non-fatal, so the application continues even on failure.
 */
export interface CacheBootstrapResult {
  /** Whether the cache bootstrap completed successfully */
  success: boolean;
  /** The parsed response data, present only if success is true */
  data?: CacheBootstrapResponse;
  /**
   * The error that occurred, present only if success is false.
   * Note: Cache errors are logged as warnings, not displayed to users.
   */
  error?: Error;
}

// =============================================================================
// Bootstrap Context State Types (for future Context-based architecture)
// =============================================================================

/**
 * Loading states for the bootstrap process.
 *
 * Provides granular visibility into which phase of bootstrap is currently
 * active, enabling optimized UI rendering and loading indicators.
 */
export interface BootstrapLoadingState {
  /** True while critical data fetch is in progress */
  isCriticalLoading: boolean;
  /** True while cache data fetch is in progress */
  isCacheLoading: boolean;
  /** Computed property: true if any phase is currently loading */
  isAnyLoading: boolean;
}

/**
 * Completion flags for bootstrap phases.
 *
 * These flags persist across component unmount/remount cycles (React Strict Mode)
 * to prevent redundant fetches after successful completion.
 */
export interface BootstrapCompletionState {
  /** True if critical bootstrap has completed successfully */
  hasCriticalCompleted: boolean;
  /** True if cache bootstrap has completed successfully */
  hasCacheCompleted: boolean;
  /** Computed property: true if both phases completed successfully */
  isFullyBootstrapped: boolean;
}

/**
 * Error state for bootstrap operations.
 *
 * Only critical bootstrap errors are considered fatal. Cache errors are
 * logged but do not prevent the application from functioning.
 */
export interface BootstrapErrorState {
  /** True if a fatal error occurred during critical bootstrap */
  hasFatalError: boolean;
  /** The critical bootstrap error, if one occurred */
  criticalError: Error | null;
  /** The cache bootstrap error, if one occurred (non-fatal, for logging) */
  cacheError: Error | null;
}

/**
 * Complete state for the Bootstrap Context.
 *
 * This interface represents all state managed by the Bootstrap Context,
 * combining loading states, completion flags, and error information.
 *
 * The context provides a centralized source of truth for bootstrap state,
 * replacing the module-level variables in the current implementation.
 */
export interface BootstrapContextState
  extends BootstrapLoadingState,
    BootstrapCompletionState,
    BootstrapErrorState {
  /**
   * The current phase of bootstrap.
   * - 'idle': No bootstrap in progress
   * - 'critical': Critical phase is loading
   * - 'cache': Cache phase is loading (critical complete)
   * - 'complete': All phases finished
   * - 'error': A fatal error occurred
   */
  phase: BootstrapPhase;
}

/**
 * Bootstrap phase enumeration.
 *
 * Represents the current state of the bootstrap process as a finite state machine.
 */
export type BootstrapPhase =
  | 'idle'
  | 'critical'
  | 'cache'
  | 'complete'
  | 'error';

// =============================================================================
// Reducer Action Types (for future Context-based architecture)
// =============================================================================

/**
 * Action to start the critical bootstrap phase.
 */
export interface StartCriticalAction {
  type: 'START_CRITICAL';
}

/**
 * Action dispatched when critical bootstrap completes successfully.
 */
export interface CriticalSuccessAction {
  type: 'CRITICAL_SUCCESS';
  payload: CriticalBootstrapResponse;
}

/**
 * Action dispatched when critical bootstrap fails with an error.
 */
export interface CriticalErrorAction {
  type: 'CRITICAL_ERROR';
  payload: Error;
}

/**
 * Action to start the cache bootstrap phase.
 * Only valid after critical bootstrap has completed.
 */
export interface StartCacheAction {
  type: 'START_CACHE';
}

/**
 * Action dispatched when cache bootstrap completes successfully.
 */
export interface CacheSuccessAction {
  type: 'CACHE_SUCCESS';
  payload: CacheBootstrapResponse;
}

/**
 * Action dispatched when cache bootstrap fails.
 * Note: Cache failures are non-fatal - the phase is set to 'complete'.
 */
export interface CacheErrorAction {
  type: 'CACHE_ERROR';
  payload: Error;
}

/**
 * Action to reset all bootstrap state.
 * Used when logging out or when explicitly restarting bootstrap.
 */
export interface ResetAction {
  type: 'RESET';
}

/**
 * Action dispatched when a bootstrap request is aborted.
 * This can happen on component unmount or explicit cancellation.
 */
export interface AbortAction {
  type: 'ABORT';
  /** Which phase was aborted */
  payload: 'critical' | 'cache';
}

/**
 * Union type for all bootstrap reducer actions.
 *
 * This type enables type-safe action handling in the reducer function
 * with proper payload type inference for each action type.
 *
 * @example
 * ```tsx
 * function bootstrapReducer(state: BootstrapContextState, action: BootstrapAction): BootstrapContextState {
 *   switch (action.type) {
 *     case 'START_CRITICAL':
 *       return { ...state, isCriticalLoading: true, phase: 'critical' };
 *     case 'CRITICAL_SUCCESS':
 *       // action.payload is typed as CriticalBootstrapResponse
 *       return { ...state, isCriticalLoading: false, hasCriticalCompleted: true };
 *     // ... other cases
 *   }
 * }
 * ```
 */
export type BootstrapAction =
  | StartCriticalAction
  | CriticalSuccessAction
  | CriticalErrorAction
  | StartCacheAction
  | CacheSuccessAction
  | CacheErrorAction
  | ResetAction
  | AbortAction;

// =============================================================================
// Retry Configuration Types
// =============================================================================

/**
 * Configuration for fetch retry logic with exponential backoff.
 *
 * These settings control how the bootstrap process handles transient
 * failures such as network errors or temporary server unavailability.
 */
export interface RetryConfig {
  /**
   * Maximum number of retry attempts for transient failures.
   * @default 3
   */
  maxAttempts: number;

  /**
   * Base delay for exponential backoff in milliseconds.
   * Each retry waits `baseDelayMs * 2^attempt` plus jitter.
   * @default 1000
   */
  baseDelayMs: number;

  /**
   * Timeout for each fetch request in milliseconds.
   * Requests that exceed this timeout are aborted and may be retried.
   * @default 30000
   */
  timeoutMs: number;

  /**
   * Maximum jitter percentage (0-1) added to backoff delays.
   * Helps prevent thundering herd problems when multiple clients retry.
   * @default 0.3 (30%)
   */
  jitterFactor: number;
}

/**
 * Default retry configuration values.
 *
 * These defaults are appropriate for most network conditions:
 * - 3 attempts: Handles brief outages without excessive retries
 * - 1s base delay with exponential backoff: 1s, 2s, 4s between retries
 * - 30s timeout: Generous enough for slow connections
 * - 30% jitter: Spreads out retry traffic
 */
export const DEFAULT_RETRY_CONFIG: Readonly<RetryConfig> = {
  maxAttempts: 3,
  baseDelayMs: 1000,
  timeoutMs: 30000,
  jitterFactor: 0.3,
} as const;

// =============================================================================
// Bootstrap Hook Options (for future extensibility)
// =============================================================================

/**
 * Options for customizing bootstrap hook behavior.
 *
 * These options allow callers to customize retry behavior and other
 * aspects of the bootstrap process without modifying the hook itself.
 */
export interface BootstrapHookOptions {
  /**
   * Custom retry configuration.
   * If not provided, DEFAULT_RETRY_CONFIG is used.
   */
  retryConfig?: Partial<RetryConfig>;

  /**
   * Whether to skip the cache fetch phase entirely.
   * Useful for tests or scenarios where cache data isn't needed.
   * @default false
   */
  skipCache?: boolean;

  /**
   * Callback invoked when critical bootstrap completes successfully.
   * Useful for triggering side effects like navigation.
   */
  onCriticalSuccess?: (data: CriticalBootstrapResponse) => void;

  /**
   * Callback invoked when cache bootstrap completes successfully.
   */
  onCacheSuccess?: (data: CacheBootstrapResponse) => void;

  /**
   * Callback invoked when a fatal error occurs during critical bootstrap.
   */
  onError?: (error: Error) => void;
}

// =============================================================================
// Type Guards
// =============================================================================

/**
 * Type guard to check if an error is transient and should be retried.
 *
 * Transient errors include:
 * - Network errors (fetch failed, no internet)
 * - Server errors (5xx status codes)
 *
 * Non-transient errors (should NOT retry):
 * - AbortError (request was cancelled)
 * - Client errors (4xx status codes)
 * - BootstrapTimeoutError (timeout is already generous)
 *
 * @param error - The error to check
 * @returns True if the error is transient and may be retried
 */
export function isTransientError(error: unknown): boolean {
  // Network errors (fetch failed, no internet, etc.)
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }

  // AbortError means the request was cancelled (timeout or unmount) - do NOT retry
  if (error instanceof DOMException && error.name === 'AbortError') {
    return false;
  }

  // Import ApiError dynamically to avoid circular dependencies
  // This check handles both 5xx (transient) and 4xx (non-transient) errors
  if (
    error instanceof Error &&
    'status' in error &&
    typeof (error as { status: unknown }).status === 'number'
  ) {
    const status = (error as { status: number }).status;
    // Server errors (5xx) are transient
    if (status >= 500) {
      return true;
    }
    // Client errors (4xx) are NOT transient - don't retry
    if (status >= 400 && status < 500) {
      return false;
    }
  }

  // Generic Error that might be a network issue
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    // Common network-related error messages
    if (
      (message ?? []).includes('network') ||
      (message ?? []).includes('failed to fetch') ||
      (message ?? []).includes('net::') ||
      (message ?? []).includes('econnrefused') ||
      (message ?? []).includes('enotfound')
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Type guard to check if a value is a valid CriticalBootstrapResponse.
 *
 * Validates the structure of API responses before processing.
 *
 * @param value - The value to check
 * @returns True if the value matches CriticalBootstrapResponse structure
 */
export function isCriticalBootstrapResponse(
  value: unknown
): value is CriticalBootstrapResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const obj = value as Record<string, unknown>;

  return (
    Array.isArray(obj.images) &&
    typeof obj.session === 'object' &&
    obj.session !== null &&
    typeof obj.settings === 'object' &&
    obj.settings !== null
  );
}

/**
 * Type guard to check if a value is a valid CacheBootstrapResponse.
 *
 * Validates the structure of API responses before processing.
 * Since CacheBootstrapResponse has optional fields, we only require
 * that it's a non-null object with at least one expected array field.
 *
 * @param value - The value to check
 * @returns True if the value matches CacheBootstrapResponse structure
 */
export function isCacheBootstrapResponse(
  value: unknown
): value is CacheBootstrapResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const obj = value as Record<string, unknown>;

  // Check for at least some expected cache data arrays (all are optional)
  // A valid cache response should have at least one of these fields as an array
  const expectedArrayFields = [
    'items', 'spells', 'treasures', 'moons', 'weatherConditions',
    'monsterRoster', 'deities', 'imageCategories', 'loreCategories',
    'soundscapeCategories', 'terrainTypes', 'soundscapes'
  ];

  return (expectedArrayFields ?? []).some(field =>
    obj[field] === undefined || Array.isArray(obj[field])
  );
}

// =============================================================================
// Initial State Factory
// =============================================================================

/**
 * Creates the initial state for the Bootstrap Context.
 *
 * Factory function to ensure each context instance gets a fresh state object.
 *
 * @returns A new BootstrapContextState with all values set to their defaults
 */
export function createInitialBootstrapState(): BootstrapContextState {
  return {
    // Loading states
    isCriticalLoading: false,
    isCacheLoading: false,
    isAnyLoading: false,

    // Completion states
    hasCriticalCompleted: false,
    hasCacheCompleted: false,
    isFullyBootstrapped: false,

    // Error states
    hasFatalError: false,
    criticalError: null,
    cacheError: null,

    // Phase
    phase: 'idle',
  };
}
